
import React, { useState } from "react";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import SEOHead from "@/components/common/SEOHead";
import { Search, Phone, Mail, MessageCircle, FileText, Camera, Video, Settings, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const HelpCenter = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const helpCategories = [
    {
      icon: Camera,
      title: "Fotografia Imobiliária",
      description: "Dúvidas sobre sessões fotográficas, preparação e entrega",
      articles: [
        "Como preparar meu imóvel para a sessão?",
        "Quantas fotos recebo no pacote?",
        "Qual o prazo de entrega das fotos?",
        "Posso solicitar fotos específicas?"
      ]
    },
    {
      icon: Video,
      title: "Vídeos e Tours 360°",
      description: "Informações sobre vídeos com drone e tours virtuais",
      articles: [
        "Como funciona o tour virtual 360°?",
        "Quando usar vídeos com drone?",
        "Diferença entre vídeo e tour virtual",
        "Equipamentos utilizados nos vídeos"
      ]
    },
    {
      icon: Settings,
      title: "CRM Imobiliário",
      description: "Suporte técnico e dúvidas sobre o sistema TocToc",
      articles: [
        "Como acessar o CRM pela primeira vez?",
        "Gerenciar leads e clientes",
        "Configurar automações",
        "Relatórios e estatísticas"
      ]
    },
    {
      icon: FileText,
      title: "Documentação do Imóvel",
      description: "Informações sobre laudos e registros fotográficos",
      articles: [
        "O que inclui a documentação completa?",
        "Prazo de entrega do laudo",
        "Documentos necessários",
        "Agendamento da documentação"
      ]
    }
  ];

  const commonQuestions = [
    {
      question: "Quais cidades vocês atendem?",
      answer: "Atendemos Balneário Camboriú, Itajaí, Itapema, Porto Belo e região. Para outras localidades, consulte disponibilidade."
    },
    {
      question: "Como funciona o pagamento?",
      answer: "Aceitamos cartão, PIX, transferência e boleto. Para empresas, oferecemos condições especiais."
    },
    {
      question: "Posso cancelar ou reagendar?",
      answer: "Sim, com antecedência mínima de 24h. Cancelamentos em cima da hora podem ter taxa."
    },
    {
      question: "Vocês trabalham finais de semana?",
      answer: "Sim, atendemos sábados mediante agendamento. Domingos e feriados sob consulta."
    }
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": "Central de Ajuda - TocToc Fotografia Imobiliária",
    "description": "Central de ajuda completa sobre fotografia imobiliária, vídeos e CRM em Balneário Camboriú, Itajaí, Itapema e Porto Belo",
    "mainEntity": commonQuestions.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="Central de Ajuda | Suporte Fotografia Imobiliária Balneário Camboriú SC"
        description="🆘 Central de ajuda completa TocToc: tutoriais, dúvidas frequentes e suporte para fotografia imobiliária, vídeos e CRM em Balneário Camboriú, Itajaí, Itapema e Porto Belo. Encontre respostas rápidas ou fale conosco! 📞 (47) 99736-7396"
        keywords="central ajuda fotografia imobiliária, suporte TocToc Balneário Camboriú, help desk fotos imóveis Itajaí, tutoriais CRM imobiliário Itapema, dúvidas vídeos drone Porto Belo, atendimento Santa Catarina, FAQ arquitetura"
        canonicalUrl="/centraldeajuda"
        schema={schema}
      />
      
      <div className="min-h-screen flex flex-col">
        <TocTocHeader />
        <main className="flex-1">
          {/* Hero Section */}
          <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Central de <span className="text-[#FF5605]">Ajuda</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed mb-8">
                  Encontre respostas rápidas sobre fotografia imobiliária, vídeos e CRM.
                  Estamos aqui para ajudar você em Balneário Camboriú e região!
                </p>
                
                {/* Search Bar */}
                <div className="relative max-w-2xl mx-auto">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Digite sua dúvida ou palavra-chave..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-4 py-4 text-lg bg-white text-[#323031] rounded-xl border-0 focus:ring-2 focus:ring-[#FF5605]"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <section className="py-12 bg-white">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="grid md:grid-cols-3 gap-6">
                <Button className="flex items-center justify-center space-x-3 p-6 bg-[#FF5605] hover:bg-[#E14D04] text-white rounded-xl h-auto">
                  <Phone className="h-6 w-6" />
                  <div className="text-left">
                    <div className="font-semibold">Falar por Telefone</div>
                    <div className="text-sm opacity-90">(47) 99736-7396</div>
                  </div>
                </Button>
                
                <Button variant="outline" className="flex items-center justify-center space-x-3 p-6 border-[#FF5605] text-[#FF5605] hover:bg-[#FF5605] hover:text-white rounded-xl h-auto">
                  <MessageCircle className="h-6 w-6" />
                  <div className="text-left">
                    <div className="font-semibold">WhatsApp</div>
                    <div className="text-sm opacity-70">Atendimento rápido</div>
                  </div>
                </Button>
                
                <Button variant="outline" className="flex items-center justify-center space-x-3 p-6 border-[#FF5605] text-[#FF5605] hover:bg-[#FF5605] hover:text-white rounded-xl h-auto">
                  <Mail className="h-6 w-6" />
                  <div className="text-left">
                    <div className="font-semibold">Enviar E-mail</div>
                    <div className="text-sm opacity-70">contato@toctocbr.com</div>
                  </div>
                </Button>
              </div>
            </div>
          </section>

          {/* Help Categories */}
          <section className="py-16 bg-gray-50">
            <div className="container max-w-7xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-4">
                  Categorias de <span className="text-[#FF5605]">Ajuda</span>
                </h2>
                <p className="text-lg text-[#5B5A59]">
                  Selecione a categoria relacionada à sua dúvida
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {helpCategories.map((category, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group">
                    <div className="w-12 h-12 bg-[#FF5605]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF5605]/20 transition-colors">
                      <category.icon className="h-6 w-6 text-[#FF5605]" />
                    </div>
                    
                    <h3 className="text-xl font-bold text-[#323031] mb-3">
                      {category.title}
                    </h3>
                    
                    <p className="text-[#5B5A59] mb-4">
                      {category.description}
                    </p>
                    
                    <ul className="space-y-2">
                      {category.articles.map((article, idx) => (
                        <li key={idx} className="text-sm text-[#5B5A59] hover:text-[#FF5605] cursor-pointer transition-colors">
                          • {article}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Common Questions */}
          <section className="py-16 bg-white">
            <div className="container max-w-4xl mx-auto px-4">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-[#323031] mb-4">
                  Perguntas <span className="text-[#FF5605]">Frequentes</span>
                </h2>
                <p className="text-lg text-[#5B5A59]">
                  Respostas rápidas para as dúvidas mais comuns
                </p>
              </div>
              
              <div className="space-y-6">
                {commonQuestions.map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-2xl p-6">
                    <h3 className="text-lg font-semibold text-[#323031] mb-3 flex items-center">
                      <HelpCircle className="h-5 w-5 text-[#FF5605] mr-3 flex-shrink-0" />
                      {item.question}
                    </h3>
                    <p className="text-[#5B5A59] leading-relaxed ml-8">
                      {item.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Support */}
          <section className="py-16 bg-[#323031] text-white">
            <div className="container max-w-7xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Não encontrou o que <span className="text-[#FF5605]">procurava</span>?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Nossa equipe de suporte está pronta para ajudar você com qualquer dúvida
                sobre fotografia imobiliária, vídeos ou CRM.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div>
                  <h4 className="font-semibold mb-2">Horário de Atendimento</h4>
                  <p className="text-white/80">Segunda a Sexta: 8h às 18h<br />Sábado: 9h às 14h</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Tempo de Resposta</h4>
                  <p className="text-white/80">WhatsApp: Até 2h<br />E-mail: Até 24h</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Cidades Atendidas</h4>
                  <p className="text-white/80">Balneário Camboriú, Itajaí<br />Itapema, Porto Belo</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-8 py-3 rounded-xl">
                  Falar no WhatsApp
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#323031] px-8 py-3 rounded-xl">
                  Enviar E-mail
                </Button>
              </div>
            </div>
          </section>
        </main>
        <TocTocFooter />
      </div>
    </>
  );
};

export default HelpCenter;
